import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

// SupporterHub hosts the sign-up form. Both values are used for the iframe src
// and for validating inbound postMessage events, so keep them in one place.
var SH_ORIGIN = 'https://operationsmile.supporterhub.net.au';
var SH_PATH = '/pages/website-sign-ups';

export default function signupModal() {
  const modal = document.getElementById('signupModal');
  if (!modal) return;

  // Remove inline display:none (FOUC prevention) — CSS takes over from here
  modal.style.removeProperty('display');

  const content = modal.querySelector('.signup-modal__content');
  const iframe = modal.querySelector('#pages-website-sign-ups-iframe');
  const openBtns = document.querySelectorAll('[data-open-signup-modal]');
  const closeBtns = modal.querySelectorAll('[data-close-signup-modal]');
  var triggerElement = null;
  var iframeLoaded = false;

  // Forward attribution params from the parent URL into the hosted form.
  function buildSrc() {
    var parentParams = new URLSearchParams(window.location.search);
    var iframeParams = new URLSearchParams();

    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'shcart']
      .forEach(function(p) {
        if (parentParams.get(p)) iframeParams.set(p, parentParams.get(p));
      });

    if (parentParams.get('source')) {
      iframeParams.set('source', parentParams.get('source'));
    } else if (parentParams.get('utm_source')) {
      iframeParams.set('source', parentParams.get('utm_source'));
    }

    var query = iframeParams.toString();
    return query ? SH_ORIGIN + SH_PATH + '?' + query : SH_ORIGIN + SH_PATH;
  }

  function ensureIframeLoaded() {
    if (iframeLoaded || !iframe) return;
    iframe.src = buildSrc();
    iframeLoaded = true;
  }

  // Warm the form before it is asked for. SupporterHub takes 4-5s to load cold
  // (~1s of that is their server's TTFB), so loading on click means the visitor
  // watches an empty panel for the whole of it.
  //
  // The trigger is the sign-up button approaching the viewport rather than page
  // load, so only visitors who reach the footer fetch it. Safe for sizing: the
  // form posts no height at all while the modal is display:none, and posts a
  // correct one ~0.4s after it opens. Verified rather than assumed.
  function preloadWhenNear() {
    if (!('IntersectionObserver' in window) || !openBtns.length) return;

    var observer = new IntersectionObserver(function(entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          ensureIframeLoaded();
          observer.disconnect();
          return;
        }
      }
    }, { rootMargin: '800px 0px' });

    openBtns.forEach(function(btn) { observer.observe(btn); });
  }

  function open() {
    triggerElement = document.activeElement;
    ensureIframeLoaded();
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    disableBodyScroll(content);

    var closeBtn = modal.querySelector('.signup-modal__close');
    if (closeBtn) closeBtn.focus();

    document.addEventListener('keydown', handleKeydown);
  }

  function close() {
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-open');
    enableBodyScroll(content);
    document.removeEventListener('keydown', handleKeydown);

    if (triggerElement) {
      triggerElement.focus();
      triggerElement = null;
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      close();
    }

    if (e.key === 'Tab') {
      // Focus inside the iframe is the vendor's to manage — stepping in here
      // would fight the hosted form's own tab order.
      if (document.activeElement === iframe) return;

      var focusable = modal.querySelectorAll(
        'button:not([tabindex="-1"]), [href], iframe, input:not([tabindex="-1"]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      var visibleFocusable = Array.from(focusable).filter(function(el) {
        return el.offsetParent !== null;
      });
      var first = visibleFocusable[0];
      var last = visibleFocusable[visibleFocusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // Single listener for every message SupporterHub sends. The origin check
  // matters: GTM, YouTube and the GiveNow donation embed all post messages too.
  // NOTE: no `typeof` anywhere in this file. babel-preset-airbnb 2.6 emits a
  // self-recursive _typeof helper under @babel/core 7.29, which blows the stack
  // at runtime. Property-existence checks below do the same job safely.
  function handleMessage(event) {
    if (event.origin !== SH_ORIGIN) return;

    var data = event.data;
    if (!data) return;

    // Auto-size the iframe to its content. SupporterHub's own snippet does no
    // type check on height, so accept a numeric string as well as a number.
    if (data.url_path && String(data.url_path).indexOf(SH_PATH) === 0) {
      var height = Number(data.height);
      if (isFinite(height) && height > 0) {
        iframe.style.height = height + 'px';
        // Drop the min-height floor now that the real height is known, so the
        // modal tracks the form exactly instead of being padded out to 42rem.
        iframe.classList.add('is-sized');
      }
      return;
    }

    // Multi-step forms ask the parent to scroll back to the top of the form.
    // Inside a modal that means scrolling the modal body, not the page.
    if (data.action === 'scrollToTop') {
      content.scrollTop = 0;
      return;
    }

    // Social sharing inside the iframe needs the parent page URL.
    if (data.type === 'ss_request_parent_url' && event.source) {
      event.source.postMessage(
        { type: 'ss_parent_url_response', url: window.location.href },
        SH_ORIGIN
      );
    }
  }

  window.addEventListener('message', handleMessage, false);
  openBtns.forEach(function(btn) { btn.addEventListener('click', open); });
  closeBtns.forEach(function(btn) { btn.addEventListener('click', close); });
  // open() still calls ensureIframeLoaded(), so a click that beats the
  // preload — or a browser without IntersectionObserver — still works.
  preloadWhenNear();
}
